'use client';
import { useEffect, useState } from 'react';
import { Range, getTrackBackground } from 'react-range';
import { FaPlus, FaMinus } from 'react-icons/fa6';

const STEP = 100;
const MIN = 0;
const MAX = 10000;

interface PriceRangeFilterProps {
  onPriceChange?: (
    min: number | null,
    max: number | null
  ) => Promise<void> | void;
  initialMin?: number;
  initialMax?: number;
}

const PriceRangeFilter = ({
  onPriceChange,
  initialMin = 0,
  initialMax = 10000,
}: PriceRangeFilterProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [values, setValues] = useState<number[]>([initialMin, initialMax]);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    setValues([initialMin, initialMax]);
  }, [initialMin, initialMax]);

  const handleCollapse = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsCollapsed(!isCollapsed);
  };

  const handleApply = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!onPriceChange) return;

    setIsApplying(true);
    try {
      const [min, max] = values;
      const isMinDifferent = min !== initialMin;
      const isMaxDifferent = max !== initialMax;

      // Wait for the price change to complete
      await onPriceChange(
        !isMinDifferent && !isMaxDifferent ? null : min,
        !isMinDifferent && !isMaxDifferent ? null : max
      );
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div
      className={`sidebar-filter sidebar-filter--price sidebar-filter--collapsible mb-40 ${isCollapsed ? 'is-collapsed' : ''}`}
    >
      <div
        className="sidebar-filter__header d-flex justify-between align-items-center cursor-pointer"
        onClick={handleCollapse}
      >
        <h3 className="sidebar-filter__title mb-0">Pricing</h3>
        <button
          type="button"
          className="sidebar-filter__toggle btn btn-link p-0"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleCollapse(e);
          }}
        >
          {isCollapsed ? <FaPlus size={14} /> : <FaMinus size={14} />}
        </button>
      </div>

      {!isCollapsed && (
        <div className="sidebar-filter__content mt-3">
          <Range
            values={values}
            step={STEP}
            min={MIN}
            max={MAX}
            onChange={setValues}
            renderTrack={({ props, children }) => (
              <div
                onMouseDown={props.onMouseDown}
                onTouchStart={props.onTouchStart}
                className="price-range__track-wrapper"
              >
                <div
                  ref={props.ref}
                  className="price-range__track"
                  style={{
                    background: getTrackBackground({
                      values,
                      colors: ['#ccc', '#ec407a', '#ccc'],
                      min: MIN,
                      max: MAX,
                    }),
                  }}
                >
                  {children}
                </div>
              </div>
            )}

            renderThumb={({ props }) => {
              const { key, ...rest } = props;
              return (
                <div
                  key={key}
                  {...rest}
                  className="price-range__thumb"
                />
              );
            }
             }

          />

          <div className="d-flex flex-wrap gap-2 justify-content-between mt-2 mb-3">
            <span>
              <span className="text-mute">From:</span>{' '}
              <span className="fw-medium">৳{values[0]}</span>
            </span>
            <span>
              <span className="text-mute">To: </span>{' '}
              <span className="fw-medium">৳{values[1]}</span>
            </span>
          </div>

          <button
            type="button"
            onClick={handleApply}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={isApplying}
            className="trk-btn trk-btn--primary w-100"
          >
            {isApplying ? 'Applying......' : 'Apply Price Filter'}
          </button>
        </div>
      )}
    </div>
  );
};
export default PriceRangeFilter;
